import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import Seo from '../components/Seo'
import { FadeIn } from '../components/Motion'

const content = `## 1. Agreement to Terms

These Terms of Service ("Terms") constitute a legally binding agreement between PrismScope.ai, Inc., a Wisconsin corporation ("PrismScope," "we," "our," or "us"), and you, the individual or entity accessing or using the PrismScope Rays platform and associated website at prismscope.ai (collectively, the "Services").

By creating an account, clicking "I Agree," or otherwise accessing or using the Services, you represent that:

1. You have read and understood these Terms;
2. You have the legal authority to enter into this agreement on behalf of yourself or your organization;
3. You agree to be bound by these Terms and our [Privacy Policy](/privacy), which is incorporated herein by reference.

**If you do not agree to these Terms, do not access or use the Services.**

## 2. Definitions

- **"Subscriber"** means a business or individual who has created an account to access and use the PrismScope platform.
- **"Respondent"** means an individual who participates in a PrismScope-powered research session deployed by a Subscriber.
- **"Campaign"** means a configured research project consisting of one or more AI-assisted interview sessions deployed by a Subscriber.
- **"Interview Data"** means transcripts, recordings, responses, and associated metadata generated through Respondent participation in a Campaign.
- **"Subscriber Content"** means all content, data, campaign configurations, questions, and materials that a Subscriber uploads, creates, or submits through the Services.
- **"PrismScope IP"** means the platform, software, algorithms, models, documentation, interface designs, trademarks, and all other intellectual property owned or licensed by PrismScope.
- **"Authorized Users"** means individuals whom a Subscriber has authorized to access the platform under its account.

## 3. Account Registration and Eligibility

### 3.1 Eligibility

You must be at least 18 years of age and capable of entering into a binding contract to use the Services. The Services are intended for business use.

### 3.2 Account Responsibility

You are responsible for:

- Maintaining the confidentiality of your account credentials
- All activity that occurs under your account, whether or not you authorized it
- Promptly notifying PrismScope through our [contact page](/contact) of any unauthorized access or suspected breach

### 3.3 Accurate Information

You agree to provide accurate, current, and complete information during registration and to keep that information updated. Accounts registered with false or misleading information may be suspended or terminated.

## 4. Subscriptions, Pricing, and Payment

### 4.1 Subscription Plans

PrismScope offers the Services on a subscription basis. Plan details, features, and pricing are published at prismscope.ai/pricing and are subject to change with reasonable notice.

### 4.2 Billing

Subscriptions are billed in advance on a monthly or annual cycle, as selected at checkout. Payment is processed by Stripe, Inc.

### 4.3 Renewals and Cancellations

Subscriptions automatically renew at the end of each billing period unless you cancel before the renewal date via your account dashboard. No prorated refunds are issued for the remainder of a prepaid period unless required by applicable law.

### 4.4 Price Changes

PrismScope may change subscription pricing with at least 30 days' written notice. Continued use after the price change takes effect constitutes acceptance of the new pricing.

### 4.5 Taxes

Quoted prices exclude applicable sales, use, VAT, or other taxes. You are responsible for all taxes associated with your use of the Services, except for taxes based on PrismScope's net income.

### 4.6 Late Payments

Overdue balances accrue interest at 1.5% per month (or the maximum rate permitted by law, if lower). PrismScope reserves the right to suspend access for accounts with overdue balances.

## 5. Permitted Use and Restrictions

### 5.1 License Grant

Subject to your compliance with these Terms and payment of applicable fees, PrismScope grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your internal business research and intelligence purposes.

### 5.2 Prohibited Conduct

You agree not to:

- Use the Services to collect data from individuals who have not properly consented
- Deploy Campaigns targeting individuals under the age of 18 without verifiable parental consent
- Conduct deceptive research, impersonate organizations, or mislead Respondents
- Collect Protected Health Information (PHI) without first executing a Business Associate Agreement
- Reverse engineer, decompile, or disassemble the platform or its underlying AI models
- Access the Services through automated means without prior authorization
- Resell, sublicense, or provide third-party access without a separate reseller agreement
- Use the Services in violation of applicable law
- Upload malware, viruses, or any code designed to interfere with the Services
- Attempt to gain unauthorized access to any portion of the Services

### 5.3 Research Compliance

Subscribers conducting research involving human subjects are solely responsible for applicable research ethics requirements, including IRB review and informed consent.

### 5.4 Employment and HR Use

Subscribers using the Services for employee engagement research are solely responsible for compliance with applicable employment law.

## 6. Subscriber Content and Data Ownership

### 6.1 Ownership

You retain all ownership rights in your Subscriber Content, including Campaign configurations, questions, and Interview Data. PrismScope claims no ownership interest in your content.

### 6.2 License to PrismScope

You grant PrismScope a limited, non-exclusive, worldwide license to process your Subscriber Content solely to provide, operate, and maintain the Services. This license terminates when you delete your content or close your account.

### 6.3 Anonymized Data

PrismScope may use de-identified and aggregated data — from which no individual can reasonably be identified — for platform improvement, model calibration, and product development. Subscribers may opt out by written notice through our [contact page](/contact).

### 6.4 Respondent Data Obligations

You represent and warrant that:

- You have obtained all legally required consents from Respondents before deploying any Campaign
- You have a lawful basis for collecting and processing Respondent data
- You will provide Respondents with disclosures required by applicable law
- You will honor Respondent data rights requests submitted to you

## 7. PrismScope Intellectual Property

PrismScope retains all right, title, and interest in PrismScope IP, including the platform, software, AI models, methodology, interface designs, branding, and documentation. These Terms do not transfer any intellectual property rights to you.

If you provide suggestions or feedback regarding the Services, you grant PrismScope a perpetual, irrevocable, royalty-free license to use and commercialize that feedback.

## 8. Confidentiality

"Confidential Information" means any non-public information disclosed by one party to the other in connection with the Services. Each party agrees to hold the other's Confidential Information in strict confidence and use it only in connection with these Terms.

Confidentiality obligations do not apply to information that is publicly available, independently developed, or required to be disclosed by law.

## 9. Term and Termination

### 9.1 Termination by Subscriber

You may cancel your subscription at any time through your account dashboard. Termination takes effect at the end of the current billing period.

### 9.2 Termination by PrismScope

PrismScope may suspend or terminate your access immediately if you materially breach these Terms, fail to pay fees when due, or if continued access poses a security, legal, or reputational risk.

### 9.3 Effect of Termination

Upon termination, your license ends immediately. You may request export of your data within 60 days, after which it will be deleted from active systems.

## 10. Disclaimers

**THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE." PRISMSCOPE DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.**

AI outputs are probabilistic in nature. You are solely responsible for reviewing, validating, and acting upon any AI-generated analysis or recommendation. PrismScope outputs do not constitute legal, HR, financial, medical, or other professional advice.

## 11. Limitation of Liability

IN NO EVENT WILL PRISMSCOPE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES. PRISMSCOPE'S TOTAL LIABILITY WILL NOT EXCEED THE GREATER OF: (A) FEES PAID IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR (B) $100.

## 12. Indemnification

You agree to defend, indemnify, and hold harmless PrismScope from claims arising out of your use of the Services in violation of these Terms, your Subscriber Content, your failure to obtain proper consents, or your violation of applicable law.

## 13. Dispute Resolution

### 13.1 Governing Law

These Terms are governed by the laws of the State of Wisconsin. Any legal proceedings will be brought exclusively in the state or federal courts in Dane County, Wisconsin.

### 13.2 Class Action Waiver

**Any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.**

### 13.3 Time Limitation

Any claim must be brought within one year of the date on which the cause of action accrued.

## 14. General Provisions

- **Entire Agreement:** These Terms, together with the Privacy Policy and any executed agreements, constitute the entire agreement between you and PrismScope.
- **Amendments:** PrismScope may modify these Terms with at least 15 days' notice. Continued use constitutes acceptance.
- **Severability:** If any provision is found unenforceable, the remaining provisions continue in full force.
- **Assignment:** You may not assign these Terms without our consent. PrismScope may assign in connection with a merger or acquisition.
- **Force Majeure:** Neither party is liable for delays caused by circumstances beyond reasonable control.

## 15. Contact

For questions about these Terms:

**PrismScope.ai, Inc.**
Madison, Wisconsin
[Contact us](/contact)
`

export default function TermsOfService() {
  return (
    <div>
      <Seo
        title="Terms of Service"
        description="The terms governing use of the PrismScope Rays platform and prismscope.ai, operated by PrismScope.ai, Inc."
        path="/terms"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b-2 border-intelligence-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-xs font-mono uppercase tracking-widest text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Terms of Service</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-intelligence-500 mb-4">
              Legal · PrismScope.ai, Inc.
            </p>
            <h1 className="text-5xl md:text-6xl font-bold text-intelligence-900 mb-6 tracking-tight leading-[0.95]">
              Terms of{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[#3b82f6] via-[#8b5cf6] to-[#00008B] bg-clip-text text-transparent">
                  service
                </span>
                <span
                  aria-hidden="true"
                  className="absolute left-0 right-0 bottom-1 md:bottom-2 h-3 md:h-4 bg-[#8b5cf6]/25 -z-0 -skew-y-1"
                />
              </span>
              <span className="text-[#00008B]">.</span>
            </h1>
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-2 px-3 py-1 border-2 border-intelligence-900 bg-white text-xs font-mono uppercase tracking-widest text-intelligence-900 rounded-full shadow-[2px_2px_0_0_#3b82f6]">
                Effective · March 1, 2026
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1 border-2 border-intelligence-900 bg-white text-xs font-mono uppercase tracking-widest text-intelligence-900 rounded-full shadow-[2px_2px_0_0_#8b5cf6]">
                Updated · March 1, 2026
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Content */}
      <section className="bg-intelligence-100 py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="relative">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-2xl bg-[#00008B] translate-x-2 translate-y-2"
              />
              <div className="relative bg-white rounded-2xl border-2 border-intelligence-900 p-8 md:p-12">
                <div className="prose prose-slate prose-headings:text-intelligence-900 prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3 prose-p:text-intelligence-600 prose-li:text-intelligence-600 prose-strong:text-intelligence-800 prose-a:text-[#3b82f6] hover:prose-a:text-[#8b5cf6] prose-table:text-sm prose-th:text-intelligence-700 prose-td:text-intelligence-600 max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  )
}
